/**
 * ZAXIA Guest Context Provider
 * Phase 4B: Contextual Recommendations
 *
 * Provides deterministic guest context for smart recommendations.
 * Frontend-only, computed from existing data, no persistence.
 *
 * Context updates when:
 * - App loads
 * - Booking is created
 * - Time of day changes
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useData } from '@/providers/DataProvider';
import type { Booking } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export type TimeOfDay = 'morning' | 'afternoon' | 'evening';

export interface GuestContext {
  // Guest identity
  guestNames: string;
  roomNumber: string;

  // Stay information
  stayDay: number;           // 1 = first day, 2 = second day, etc.
  checkInDate: string;
  checkOutDate: string;
  isCheckoutDay: boolean;    // True if today is checkout day
  isFirstDay: boolean;       // True if today is check-in day

  // Time context
  timeOfDay: TimeOfDay;
  currentHour: number;

  // Bookings
  todayBookings: Booking[];
  hasUpcomingBooking: boolean;

  // Utility
  refreshContext: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function getTimeOfDay(hour: number): TimeOfDay {
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function calculateStayDay(checkInDate: string): number {
  const checkIn = new Date(checkInDate);
  const today = new Date();

  // Reset to midnight for accurate day calculation
  checkIn.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - checkIn.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(1, diffDays + 1); // Day 1 is check-in day
}

function isToday(dateStr: string): boolean {
  const date = new Date(dateStr);
  const today = new Date();

  // Debug logging
  if (import.meta.env.DEV) {
    const result = date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();
    console.log(`[GuestContext] isToday("${dateStr}") = ${result} | parsed=${date.getDate()}/${date.getMonth()+1}/${date.getFullYear()} vs today=${today.getDate()}/${today.getMonth()+1}/${today.getFullYear()}`);
  }

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

function getTodayBookings(bookings: Booking[]): Booking[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return bookings.filter(booking => {
    const bookingDate = new Date(booking.datetime);
    return bookingDate >= today && bookingDate < tomorrow && booking.status !== 'CANCELLED';
  });
}

function hasUpcomingBookingToday(bookings: Booking[]): boolean {
  const now = new Date();

  return bookings.some(booking => {
    const bookingDate = new Date(booking.datetime);
    return bookingDate > now && booking.status !== 'CANCELLED';
  });
}

// =============================================================================
// CONTEXT
// =============================================================================

const GuestContextContext = createContext<GuestContext | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

interface GuestContextProviderProps {
  children: ReactNode;
}

export function GuestContextProvider({ children }: GuestContextProviderProps) {
  const { guest, room, stay, bookings } = useData();

  // Time tracking - update every minute
  const [currentHour, setCurrentHour] = useState(() => new Date().getHours());

  // Update hour when it changes
  useEffect(() => {
    const interval = setInterval(() => {
      const newHour = new Date().getHours();
      if (newHour !== currentHour) {
        setCurrentHour(newHour);
        console.log('[GuestContext] Time of day updated:', getTimeOfDay(newHour));
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [currentHour]);

  // Force context refresh (called after booking creation)
  const refreshContext = useCallback(() => {
    console.log('[GuestContext] Context refreshed');
    // The context will auto-update because bookings changed
    // This is just a signal for components that need to know
  }, []);

  // Compute context
  const context: GuestContext | null = guest && room && stay ? {
    // Guest identity
    guestNames: `${guest.firstName} ${guest.lastName}`,
    roomNumber: room.roomNumber,

    // Stay information
    stayDay: calculateStayDay(stay.checkInDate),
    checkInDate: stay.checkInDate,
    checkOutDate: stay.checkOutDate,
    isCheckoutDay: isToday(stay.checkOutDate),
    isFirstDay: isToday(stay.checkInDate),

    // Time context
    timeOfDay: getTimeOfDay(currentHour),
    currentHour,

    // Bookings
    todayBookings: getTodayBookings(bookings),
    hasUpcomingBooking: hasUpcomingBookingToday(bookings),

    // Utility
    refreshContext,
  } : null;

  // Log context on changes (dev only)
  useEffect(() => {
    if (context && import.meta.env.DEV) {
      console.log('[GuestContext] Context computed:', {
        guestNames: context.guestNames,
        roomNumber: context.roomNumber,
        stayDay: context.stayDay,
        timeOfDay: context.timeOfDay,
        todayBookings: context.todayBookings.length,
        isCheckoutDay: context.isCheckoutDay,
        isFirstDay: context.isFirstDay,
      });
    }
  }, [context?.stayDay, context?.timeOfDay, context?.todayBookings.length]);

  return (
    <GuestContextContext.Provider value={context}>
      {children}
    </GuestContextContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useGuestContext(): GuestContext | null {
  return useContext(GuestContextContext);
}

/**
 * Get guest context for API calls
 * Returns a plain object that can be passed to the backend
 */
export function useGuestContextForApi() {
  const ctx = useGuestContext();

  if (!ctx) return null;

  return {
    guestName: ctx.guestNames.split(' ')[0], // First name only
    stayDay: ctx.stayDay,
    isCheckoutDay: ctx.isCheckoutDay,
    isFirstDay: ctx.isFirstDay,
    todayBookingServiceIds: ctx.todayBookings.map(b => b.serviceId),
    timeOfDay: ctx.timeOfDay,
  };
}
